<template>
  <div class="command-center" ref="rootRef">
    <!-- Notification Bell — always visible in header -->
    <button
        class="cc-bell"
        @click="openPanel('notifications')"
        :title="`${unreadCount} unread notifications`"
        :class="{ 'has-unread': unreadCount > 0 }"
    >
      <MdpsIcon name="bell" size="small" class="cc-bell-icon" onDark/>
      <transition name="badge-pop">
        <span v-if="unreadCount > 0" class="cc-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
      </transition>
      <span v-if="unreadCount > 0" class="cc-pulse"></span>
    </button>

    <!-- User Avatar -->
    <button class="cc-avatar" @click="openPanel('profile')" :title="userEmail">
      <UserAvatar :email="userEmail" :name="displayName" size="sm"/>
    </button>

    <!-- Slide Panel -->
    <transition name="panel-slide">
      <div v-if="activePanel" class="cc-panel" :class="`cc-panel--${activePanel}`">
        <!-- Panel Header with tabs -->
        <div class="cc-panel-header">
          <div class="cc-tabs">
            <button
                class="cc-tab"
                :class="{ active: activePanel === 'notifications' }"
                @click="activePanel = 'notifications'"
            >
              <MdpsIcon name="bell" size="x-small"/>
              Activity
              <span v-if="unreadCount > 0" class="cc-tab-badge">{{ unreadCount }}</span>
            </button>
            <button
                class="cc-tab"
                :class="{ active: activePanel === 'profile' }"
                @click="activePanel = 'profile'"
            >
              <MdpsIcon name="user" size="x-small"/>
              Account
            </button>
          </div>
          <button class="cc-close" @click="closePanel" aria-label="Close panel">
            <MdpsIcon name="x" size="small"/>
          </button>
        </div>

        <!-- Notifications Tab -->
        <div v-if="activePanel === 'notifications'" class="cc-panel-body">
          <!-- Quick Stats -->
          <div class="cc-stats-row" v-if="unreadCount > 0">
            <div class="cc-stat">
              <span class="cc-stat-num">{{ unreadCount }}</span>
              <span class="cc-stat-label">Unread</span>
            </div>
            <button class="cc-action-pill" @click="handleMarkAllRead">
              <MdpsIcon name="check" size="x-small"/>
              Mark all read
            </button>
          </div>

          <!-- Notification Feed -->
          <div v-if="isLoading" class="cc-loading">
            <div class="cc-loading-dots"><span></span><span></span><span></span></div>
          </div>

          <div v-else-if="notifications.length === 0" class="cc-empty">
            <div class="cc-empty-icon">
              <MdpsIcon name="bell" size="x-large"/>
            </div>
            <p class="cc-empty-title">You're all caught up</p>
            <p class="cc-empty-sub">No new notifications. We'll let you know when something happens.</p>
          </div>

          <ul v-else class="cc-feed">
            <li
                v-for="(notification, idx) in notifications.slice(0, 25)"
                :key="notification.id"
                :class="['cc-feed-item', { unread: !notification.read }]"
                :style="{ animationDelay: `${idx * 30}ms` }"
                @click="handleNotificationClick(notification)"
            >
              <div class="cc-feed-dot" :class="dotClass(notification.type)"></div>
              <div class="cc-feed-content">
                <div class="cc-feed-head">
                  <span class="cc-feed-icon">{{ iconFor(notification.type) }}</span>
                  <span class="cc-feed-time">{{ timeAgo(notification.createdAt) }}</span>
                </div>
                <p class="cc-feed-title">{{ notification.title }}</p>
                <p class="cc-feed-msg">{{ notification.message }}</p>
              </div>
            </li>
          </ul>

          <!-- Footer link -->
          <router-link to="/notifications" class="cc-panel-footer-link" @click="closePanel">
            View all notifications
          </router-link>
        </div>

        <!-- Profile Tab -->
        <div v-if="activePanel === 'profile'" class="cc-panel-body cc-profile-body">
          <div class="cc-profile-card">
            <UserAvatar :email="userEmail" :name="displayName" size="lg"/>
            <div class="cc-profile-info">
              <h3 class="cc-profile-name">{{ displayName }}</h3>
              <p class="cc-profile-email">{{ userEmail }}</p>
            </div>
          </div>

          <div class="cc-roles-section">
            <span v-for="role in roles" :key="role" class="cc-role-tag">{{ role }}</span>
          </div>

          <div class="cc-quick-actions">
            <router-link to="/profile" class="cc-quick-action" @click="closePanel">
              <div class="cc-qa-icon cc-qa-icon--profile">
                <MdpsIcon name="user" size="small"/>
              </div>
              <div class="cc-qa-text">
                <span class="cc-qa-label">My Profile</span>
                <span class="cc-qa-desc">View submissions & scores</span>
              </div>
            </router-link>

            <router-link to="/notifications" class="cc-quick-action" @click="closePanel">
              <div class="cc-qa-icon cc-qa-icon--notif">
                <MdpsIcon name="bell" size="small"/>
              </div>
              <div class="cc-qa-text">
                <span class="cc-qa-label">Notifications</span>
                <span class="cc-qa-desc">{{ unreadCount ? `${unreadCount} unread` : 'All caught up' }}</span>
              </div>
            </router-link>

            <router-link v-if="authStore.canCreateInitiative" to="/submit" class="cc-quick-action" @click="closePanel">
              <div class="cc-qa-icon cc-qa-icon--submit">
                <MdpsIcon name="plus" size="small"/>
              </div>
              <div class="cc-qa-text">
                <span class="cc-qa-label">Submit Initiative</span>
                <span class="cc-qa-desc">Share a new initiative</span>
              </div>
            </router-link>

            <button v-else type="button" class="cc-quick-action cc-quick-action--disabled" @click="handleRestrictedSubmitClick">
              <div class="cc-qa-icon cc-qa-icon--submit">
                <MdpsIcon name="plus" size="small"/>
              </div>
              <div class="cc-qa-text">
                <span class="cc-qa-label">Submit Initiative</span>
                <span class="cc-qa-desc">Contributor role required</span>
              </div>
            </button>

            <router-link to="/contact" class="cc-quick-action" @click="closePanel">
              <div class="cc-qa-icon cc-qa-icon--support">
                <MdpsIcon name="chat-bubble" size="small"/>
              </div>
              <div class="cc-qa-text">
                <span class="cc-qa-label">Contact Support</span>
                <span class="cc-qa-desc">Questions, access, feedback</span>
              </div>
            </router-link>
          </div>

          <button class="cc-signout" @click="handleSignOut">
            <MdpsIcon name="launch" size="x-small"/>
            Sign Out
          </button>
        </div>
      </div>
    </transition>

    <!-- Backdrop -->
    <transition name="fade">
      <div v-if="activePanel" class="cc-backdrop" @click="closePanel"></div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import {ref, computed, onMounted, onUnmounted} from 'vue'
import {useRouter} from 'vue-router'
import {useNotifications} from '@/composables/useNotifications'
import {useAuthStore} from '@/stores/authStore'
import UserAvatar from '@/components/UserAvatar.vue'
import MdpsIcon from '@mdps/icon-vue3'
import type {Notification, NotificationType} from '@/types/notification'
import {showContributorUpgradeMessage} from '@/utils/roleMessages'

const router = useRouter()
const authStore = useAuthStore()
const {
  notifications,
  unreadCount,
  isLoading,
  fetchNotifications,
  fetchUnreadCount,
  markAsRead,
  markAllAsRead,
} = useNotifications()

const rootRef = ref<HTMLElement | null>(null)
const activePanel = ref<'notifications' | 'profile' | null>(null)

const userEmail = computed(() => authStore.userEmail || '')
const displayName = computed(() => {
  const p = authStore.user?.profile
  return p?.name || p?.given_name || authStore.userEmail || ''
})
const initials = computed(() => {
  const name = authStore.user?.profile?.name || authStore.userEmail || ''
  return name.split(/[\s@.]/).filter(Boolean).slice(0, 2).map((w: string) => w[0].toUpperCase()).join('')
})
const roles = computed(() => authStore.roles || [])

function openPanel(panel: 'notifications' | 'profile') {
  if (activePanel.value === panel) {
    closePanel()
    return
  }
  activePanel.value = panel
  if (panel === 'notifications') {
    fetchNotifications()
  }
}

function closePanel() {
  activePanel.value = null
}

function handleNotificationClick(notification: Notification) {
  if (!notification.read) {
    markAsRead(notification.id)
  }
  if (notification.relatedInitiativeId) {
    router.push({name: 'InitiativeDetail', params: {id: notification.relatedInitiativeId}})
    closePanel()
  } else if (notification.relatedProjectId) {
    router.push({name: 'Search'})
    closePanel()
  }
}

function handleMarkAllRead() {
  markAllAsRead()
}

function handleSignOut() {
  closePanel()
  authStore.signout()
}

function handleRestrictedSubmitClick() {
  closePanel()
  showContributorUpgradeMessage()
}

function dotClass(type: NotificationType): string {
  const map: Record<NotificationType, string> = {
    opt_in_request: 'dot-blue',
    approved: 'dot-green',
    rejected: 'dot-red',
    opted_out: 'dot-amber',
    participant_added: 'dot-green',
    participant_removed: 'dot-red',
    approval_requested: 'dot-blue',
    approval_reminder: 'dot-amber',
    approval_escalated: 'dot-red',
    initiative_status: 'dot-green',
  }
  return map[type] || 'dot-blue'
}

function iconFor(type: NotificationType): string {
  const map: Record<NotificationType, string> = {
    opt_in_request: '🙋',
    approved: '✅',
    rejected: '❌',
    opted_out: '👋',
    participant_added: '➕',
    participant_removed: '➖',
    approval_requested: '✓',
    approval_reminder: '!',
    approval_escalated: '!',
    initiative_status: '•',
  }
  return map[type] || '🔔'
}

function timeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d`
  return date.toLocaleDateString(undefined, {month: 'short', day: 'numeric'})
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && activePanel.value) {
    closePanel()
  }
}

function refreshNotifications() {
  fetchUnreadCount()
  if (activePanel.value === 'notifications') {
    fetchNotifications()
  }
}

let pollInterval: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  if (authStore.isAuthenticated) {
    fetchUnreadCount()
    pollInterval = setInterval(fetchUnreadCount, 45000)
  }
  document.addEventListener('keydown', onKeydown)
  window.addEventListener('notifications:updated', refreshNotifications)
})

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval)
  document.removeEventListener('keydown', onKeydown)
  window.removeEventListener('notifications:updated', refreshNotifications)
})
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════════
   COMMAND CENTER — Modern Notification & Profile Hub
   ═══════════════════════════════════════════════════════════════════ */

.command-center {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* ─── Bell Button ──────────────────────────────────────────────── */
.cc-bell {
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, transform 0.15s;
  color: rgba(255, 255, 255, 0.85);
}

.cc-bell:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: scale(1.05);
}

.cc-bell:active {
  transform: scale(0.95);
}

.cc-bell.has-unread .cc-bell-icon {
  animation: bell-ring 2s ease-in-out infinite;
  animation-delay: 3s;
}

@keyframes bell-ring {
  0%, 100% {
    transform: rotate(0);
  }
  5% {
    transform: rotate(12deg);
  }
  10% {
    transform: rotate(-10deg);
  }
  15% {
    transform: rotate(8deg);
  }
  20% {
    transform: rotate(-5deg);
  }
  25% {
    transform: rotate(0);
  }
}

.cc-badge {
  position: absolute;
  top: 3px;
  right: 3px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #ef4444;
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(239, 68, 68, 0.4);
  border: 2px solid #1a1f36;
}

.badge-pop-enter-active {
  animation: pop 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes pop {
  0% {
    transform: scale(0);
  }
  100% {
    transform: scale(1);
  }
}

.cc-pulse {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.4);
  animation: pulse-ring 2s ease-out infinite;
  pointer-events: none;
}

@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(2.2);
    opacity: 0;
  }
}

/* ─── Avatar Button ────────────────────────────────────────────── */
.cc-avatar {
  position: relative;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s, box-shadow 0.2s;
}

.cc-avatar:hover {
  transform: scale(1.08);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.3);
}

.cc-avatar:active {
  transform: scale(0.95);
}

.cc-avatar-text {
  color: white;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  z-index: 1;
}

.cc-avatar-ring {
  position: absolute;
  inset: -2px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.25);
  transition: border-color 0.2s;
}

.cc-avatar:hover .cc-avatar-ring {
  border-color: rgba(255, 255, 255, 0.6);
}

/* ─── Slide Panel ──────────────────────────────────────────────── */
.cc-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 400px;
  max-width: 100vw;
  background: #ffffff;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 40px rgba(0, 0, 0, 0.12), -2px 0 8px rgba(0, 0, 0, 0.06);
}

.panel-slide-enter-active {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.panel-slide-leave-active {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.panel-slide-enter-from,
.panel-slide-leave-to {
  transform: translateX(100%);
}

/* ─── Panel Header ─────────────────────────────────────────────── */
.cc-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f1f3f5;
  flex-shrink: 0;
}

.cc-tabs {
  display: flex;
  gap: 0.25rem;
  background: #f4f6f8;
  border-radius: 10px;
  padding: 3px;
}

.cc-tab {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.85rem;
  border: none;
  background: none;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.cc-tab.active {
  background: white;
  color: #1e293b;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.cc-tab:hover:not(.active) {
  color: #334155;
}

.cc-tab-badge {
  background: #ef4444;
  color: white;
  font-size: 0.62rem;
  font-weight: 700;
  min-width: 16px;
  height: 16px;
  border-radius: 99px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

.cc-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  transition: background 0.15s, color 0.15s;
}

.cc-close:hover {
  background: #f1f5f9;
  color: #475569;
}

/* ─── Panel Body ───────────────────────────────────────────────── */
.cc-panel-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

/* ─── Stats Row ────────────────────────────────────────────────── */
.cc-stats-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.25rem;
  background: linear-gradient(135deg, #f0f7ff 0%, #f5f3ff 100%);
  border-bottom: 1px solid #e8ecf2;
}

.cc-stat {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
}

.cc-stat-num {
  font-size: 1.3rem;
  font-weight: 800;
  color: #3b82f6;
}

.cc-stat-label {
  font-size: 0.78rem;
  color: #64748b;
  font-weight: 500;
}

.cc-action-pill {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: white;
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s;
}

.cc-action-pill:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

/* ─── Loading State ────────────────────────────────────────────── */
.cc-loading {
  display: flex;
  justify-content: center;
  padding: 3rem;
}

.cc-loading-dots {
  display: flex;
  gap: 6px;
}

.cc-loading-dots span {
  width: 8px;
  height: 8px;
  background: #94a3b8;
  border-radius: 50%;
  animation: dot-bounce 1.2s ease-in-out infinite;
}

.cc-loading-dots span:nth-child(2) {
  animation-delay: 0.15s;
}

.cc-loading-dots span:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes dot-bounce {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-6px);
    opacity: 1;
  }
}

/* ─── Empty State ──────────────────────────────────────────────── */
.cc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
}

.cc-empty-icon {
  margin-bottom: 1rem;
  opacity: 0.6;
}

.cc-empty-title {
  margin: 0 0 0.4rem;
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

.cc-empty-sub {
  margin: 0;
  font-size: 0.84rem;
  color: #64748b;
  line-height: 1.5;
  max-width: 260px;
}

/* ─── Feed ─────────────────────────────────────────────────────── */
.cc-feed {
  list-style: none;
  margin: 0;
  padding: 0;
}

.cc-feed-item {
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f8f9fb;
  cursor: pointer;
  transition: background 0.15s;
  animation: slide-in 0.3s ease-out both;
}

@keyframes slide-in {
  from {
    opacity: 0;
    transform: translateX(12px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.cc-feed-item:hover {
  background: #f8fafc;
}

.cc-feed-item.unread {
  background: #fafbff;
  border-left: 3px solid #3b82f6;
}

.cc-feed-item.unread:hover {
  background: #f0f4ff;
}

.cc-feed-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 5px;
  flex-shrink: 0;
}

.dot-blue {
  background: #3b82f6;
}

.dot-green {
  background: #10b981;
}

.dot-red {
  background: #ef4444;
}

.dot-amber {
  background: #f59e0b;
}

.cc-feed-content {
  flex: 1;
  min-width: 0;
}

.cc-feed-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}

.cc-feed-icon {
  font-size: 0.85rem;
}

.cc-feed-time {
  font-size: 0.7rem;
  color: #94a3b8;
  font-weight: 500;
}

.cc-feed-title {
  margin: 0;
  font-size: 0.84rem;
  font-weight: 650;
  color: #1e293b;
  line-height: 1.35;
}

.cc-feed-msg {
  margin: 0.2rem 0 0;
  font-size: 0.78rem;
  color: #64748b;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.cc-feed-link {
  display: inline-block;
  margin-top: 0.4rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #3b82f6;
  text-decoration: none;
  transition: color 0.15s;
}

.cc-feed-link:hover {
  color: #2563eb;
}

/* ─── Footer Link ──────────────────────────────────────────────── */
.cc-panel-footer-link {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.85rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #3b82f6;
  text-decoration: none;
  border-top: 1px solid #f1f5f9;
  transition: background 0.15s;
  flex-shrink: 0;
}

.cc-panel-footer-link:hover {
  background: #f8fafc;
}

/* ─── Profile Tab ──────────────────────────────────────────────── */
.cc-profile-body {
  padding: 1.5rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.cc-profile-card {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.cc-profile-avatar {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cc-profile-avatar span {
  color: white;
  font-size: 1.1rem;
  font-weight: 700;
}

.cc-profile-info {
  flex: 1;
  min-width: 0;
}

.cc-profile-name {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1e293b;
}

.cc-profile-email {
  margin: 0.15rem 0 0;
  font-size: 0.8rem;
  color: #64748b;
  word-break: break-all;
}

.cc-roles-section {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.cc-role-tag {
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 650;
  text-transform: capitalize;
  background: linear-gradient(135deg, #eff6ff, #f5f3ff);
  color: #4f46e5;
  border: 1px solid #e0e7ff;
}

/* ─── Quick Actions ────────────────────────────────────────────── */
.cc-quick-actions {
  display: grid;
  gap: 0.5rem;
}

.cc-quick-action {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.85rem;
  border-radius: 12px;
  text-decoration: none;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  transition: all 0.2s;
  width: 100%;
  font: inherit;
  text-align: left;
}

.cc-quick-action:hover {
  background: #f1f5f9;
  border-color: #e2e8f0;
  transform: translateX(3px);
}

.cc-quick-action--disabled {
  cursor: not-allowed;
  opacity: 0.58;
  color: #64748b;
}

.cc-quick-action--disabled:hover {
  background: #f8fafc;
  border-color: #e2e8f0;
  transform: none;
}

.cc-qa-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cc-qa-icon--profile {
  background: #eff6ff;
  color: #3b82f6;
}

.cc-qa-icon--notif {
  background: #fef3c7;
  color: #d97706;
}

.cc-qa-icon--stretch {
  background: #ecfdf5;
  color: #059669;
}

.cc-qa-icon--submit {
  background: #f5f3ff;
  color: #7c3aed;
}

.cc-qa-icon--support {
  background: #f1f5f9;
  color: #475569;
}

.cc-qa-text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.cc-qa-label {
  font-size: 0.87rem;
  font-weight: 600;
  color: #1e293b;
}

.cc-qa-desc {
  font-size: 0.74rem;
  color: #94a3b8;
}

/* ─── Sign Out ─────────────────────────────────────────────────── */
.cc-signout {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem;
  border-radius: 10px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #dc2626;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  margin-top: auto;
}

.cc-signout:hover {
  background: #fee2e2;
  border-color: #fca5a5;
}

/* ─── Backdrop ─────────────────────────────────────────────────── */
.cc-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.3);
  backdrop-filter: blur(2px);
  z-index: 10000;
}

.fade-enter-active {
  transition: opacity 0.2s;
}

.fade-leave-active {
  transition: opacity 0.15s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* ─── Mobile Responsive ────────────────────────────────────────── */
@media (max-width: 480px) {
  .cc-panel {
    width: 100vw;
  }

  .cc-bell, .cc-avatar {
    width: 34px;
    height: 34px;
  }

  .cc-avatar-text {
    font-size: 0.7rem;
  }
}
</style>
